import { lazy, Suspense, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RoleNavigationShell } from '../../navigation/components/RoleNavigationShell';
import { RouteLoadingFallback } from '../../shared/components/RouteLoadingFallback';
import { ROLES, hasRoleAccess } from '../roles';
import { useEntitlements } from '../../entitlements/EntitlementsContext';
import { FEATURE_KEYS } from '../../entitlements/keys';

const DashboardPanel = lazy(() =>
  import('../../dashboard/components/DashboardPanel').then((m) => ({ default: m.DashboardPanel })),
);
const EntryPage = lazy(() =>
  import('../../entry/pages/EntryPage').then((m) => ({ default: m.EntryPage })),
);
const ExitPage = lazy(() => import('../../visits/pages/ExitPage').then((m) => ({ default: m.ExitPage })));
const SupportPage = lazy(() =>
  import('../../support/pages/SupportPage').then((m) => ({ default: m.SupportPage })),
);
const AuditLogsPage = lazy(() =>
  import('../../auditLogs/pages/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })),
);
const SystemSettingsPage = lazy(() =>
  import('../../systemSettings/pages/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage })),
);
const UserManagementPage = lazy(() =>
  import('../../userManagement/pages/UserManagementPage').then((m) => ({ default: m.UserManagementPage })),
);
const CustomersPage = lazy(() =>
  import('../../customersManagement/pages/CustomersPage').then((m) => ({ default: m.CustomersPage })),
);
const WalkinPricingPlansPage = lazy(() =>
  import('../../pricing/pages/WalkinPricingPlansPage').then((m) => ({ default: m.WalkinPricingPlansPage })),
);
const DiscountsPage = lazy(() =>
  import('../../discounts/pages/DiscountsPage').then((m) => ({ default: m.DiscountsPage })),
);
const GiftCardsPage = lazy(() =>
  import('../../giftcards/pages/GiftCardsPage').then((m) => ({ default: m.GiftCardsPage })),
);
const FinancialSettingsPage = lazy(() =>
  import('../../financialSettings/pages/FinancialSettingsPage').then((m) => ({
    default: m.FinancialSettingsPage,
  })),
);
const FinancialManagementPage = lazy(() =>
  import('../../financialManagement/pages/FinancialManagementPage').then((m) => ({
    default: m.FinancialManagementPage,
  })),
);
const SalesReportsPage = lazy(() =>
  import('../../salesReports/pages/SalesReportsPage').then((m) => ({ default: m.SalesReportsPage })),
);
const UtilitiesPage = lazy(() =>
  import('../../utilities/pages/UtilitiesPage').then((m) => ({ default: m.UtilitiesPage })),
);
const IntegrationsPage = lazy(() =>
  import('../../integrations/pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
);
const BranchesPage = lazy(() =>
  import('../../branches/pages/BranchesPage').then((m) => ({ default: m.BranchesPage })),
);
const BranchSettingsPage = lazy(() =>
  import('../../branches/pages/BranchSettingsPage').then((m) => ({ default: m.BranchSettingsPage })),
);

export const AuthenticatedPanel = ({
  user,
  token,
  entrySettings,
  pricingSettings,
  activeBranch,
  onRefreshSession,
  t,
  renderHeader,
  locale,
}) => {
  /** Sidebar nav icons: keyed by each module's `id` in `src/features/navigation/icons/navIcons.js` (`NAV_ITEM_ICONS`). */
  const modules = useMemo(
    () => [
      {
        id: 'dashboard',
        labelKey: 'navDashboard',
        requiredRole: ROLES.STAFF,
        section: 'business',
        path: '/dashboard',
        render: () => (
          <DashboardPanel
            role={user.role}
            token={token}
            entrySettings={entrySettings}
            pricingSettings={pricingSettings}
            t={t}
            locale={locale}
          />
        ),
      },
      {
        id: 'entry-page',
        labelKey: 'navEntryPage',
        requiredRole: ROLES.STAFF,
        section: 'business',
        path: '/entry',
        nav: false,
        render: () => (
          <EntryPage
            token={token}
            entrySettings={entrySettings}
            pricingSettings={pricingSettings}
            userRole={user.role}
            t={t}
            locale={locale}
          />
        ),
      },
      {
        id: 'exit-page',
        labelKey: 'navExitPage',
        requiredRole: ROLES.STAFF,
        section: 'business',
        path: '/exit',
        nav: false,
        render: () => (
          <ExitPage
            token={token}
            entrySettings={entrySettings}
            pricingSettings={pricingSettings}
            userRole={user.role}
            t={t}
            locale={locale}
          />
        ),
      },
      {
        id: 'support',
        labelKey: 'navSupport',
        requiredRole: ROLES.STAFF,
        section: 'business',
        path: '/support',
        nav: false,
        render: () => <SupportPage token={token} t={t} locale={locale} role={user.role} />,
      },
      {
        id: 'customers',
        labelKey: 'navCustomers',
        requiredRole: ROLES.STAFF,
        section: 'business',
        path: '/customers',
        render: () => (
          <CustomersPage
            token={token}
            t={t}
            locale={locale}
            role={user.role}
            pricingSettings={pricingSettings}
            entrySettings={entrySettings}
          />
        ),
      },
      {
        id: 'pricing',
        labelKey: 'navPricing',
        requiredRole: ROLES.MANAGER,
        section: 'business',
        path: '/pricing',
        render: () => (
          <WalkinPricingPlansPage
            token={token}
            t={t}
            locale={locale}
            role={user.role}
            pricingSettings={pricingSettings}
            activeBranch={activeBranch}
          />
        ),
      },
      {
        id: 'gift-cards',
        labelKey: 'navGiftCards',
        requiredRole: ROLES.STAFF,
        requiredFeature: FEATURE_KEYS.GIFTCARDS_ENABLED,
        section: 'business',
        path: '/gift-cards',
        render: () => <GiftCardsPage token={token} t={t} locale={locale} role={user.role} />,
      },
      {
        id: 'discounts',
        labelKey: 'navDiscounts',
        requiredRole: ROLES.ADMIN,
        section: 'business',
        path: '/discounts',
        render: () => <DiscountsPage token={token} t={t} locale={locale} />,
      },
      {
        id: 'financial-management',
        labelKey: 'navFinancialManagement',
        requiredRole: ROLES.ADMIN,
        section: 'business',
        path: '/financial-management',
        render: () => <FinancialManagementPage token={token} t={t} locale={locale} />,
      },
      {
        id: 'sales-reports',
        labelKey: 'navSalesReports',
        requiredRole: ROLES.MANAGER,
        section: 'business',
        path: '/sales-reports',
        render: () => <SalesReportsPage token={token} t={t} locale={locale} />,
      },
      {
        id: 'utilities',
        labelKey: 'navUtilities',
        requiredRole: ROLES.STAFF,
        requiredFeature: FEATURE_KEYS.UTILITIES_ENABLED,
        section: 'business',
        path: '/utilities',
        render: () => <UtilitiesPage token={token} t={t} locale={locale} role={user.role} />,
      },
      {
        id: 'audit',
        labelKey: 'navAuditLogs',
        requiredRole: ROLES.MANAGER,
        requiredFeature: FEATURE_KEYS.MANAGEMENT_AUDIT_LOGS,
        section: 'admin',
        path: '/audit-logs',
        render: () => <AuditLogsPage token={token} t={t} locale={locale} />,
      },
      {
        id: 'financial-settings',
        labelKey: 'navFinancialSettings',
        requiredRole: ROLES.ADMIN,
        section: 'admin',
        path: '/financial-settings',
        render: () => (
          <FinancialSettingsPage
            token={token}
            t={t}
            locale={locale}
            onSettingsSaved={onRefreshSession}
          />
        ),
      },
      {
        id: 'integrations',
        labelKey: 'navIntegrations',
        requiredRole: ROLES.ADMIN,
        section: 'admin',
        path: '/integrations/*',
        render: () => <IntegrationsPage token={token} t={t} locale={locale} />,
      },
      {
        id: 'drivers-redirect',
        labelKey: 'navDrivers',
        requiredRole: ROLES.ADMIN,
        section: 'admin',
        path: '/drivers',
        nav: false,
        render: () => <Navigate to="/integrations/drivers" replace />,
      },
      {
        id: 'user-management',
        labelKey: 'navUserManagement',
        requiredRole: ROLES.ADMIN,
        section: 'admin',
        path: '/user-management',
        render: () => <UserManagementPage role={user.role} token={token} t={t} locale={locale} />,
      },
      {
        id: 'branch-settings',
        labelKey: 'navBranchSettings',
        requiredRole: ROLES.ADMIN,
        section: 'admin',
        path: '/branch-settings',
        render: () => (
          <BranchSettingsPage
            token={token}
            t={t}
            activeBranch={activeBranch}
            onSettingsSaved={onRefreshSession}
          />
        ),
      },
      {
        id: 'branches',
        labelKey: 'navBranches',
        requiredRole: ROLES.ADMIN,
        section: 'admin',
        path: '/branches',
        render: () => <BranchesPage token={token} t={t} locale={locale} />,
      },
      {
        id: 'system-settings',
        labelKey: 'navSystemSettings',
        requiredRole: ROLES.ADMIN,
        section: 'admin',
        path: '/system-settings',
        render: () => (
          <SystemSettingsPage token={token} t={t} locale={locale} onSettingsSaved={onRefreshSession} />
        ),
      },
    ],
    [activeBranch, entrySettings, locale, onRefreshSession, pricingSettings, t, token, user.role, user],
  );
  const { isEnabled } = useEntitlements();
  const roleModules = useMemo(
    () =>
      modules
        .filter((module) => hasRoleAccess(user.role, module.requiredRole))
        .map((module) => ({
          ...module,
          locked: Boolean(module.requiredFeature && !isEnabled(module.requiredFeature)),
        })),
    [isEnabled, modules, user.role],
  );
  const defaultPath =
    roleModules.find((module) => !module.locked)?.path || roleModules[0]?.path || '/dashboard';

  return (
    <RoleNavigationShell role={user.role} modules={roleModules} t={t} renderHeader={renderHeader}>
      <Suspense fallback={<RouteLoadingFallback t={t} />}>
        <Routes>
          {roleModules.map((module) => (
            <Route key={module.id} path={module.path} element={module.render()} />
          ))}
          <Route path="/" element={<Navigate to={defaultPath} replace />} />
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      </Suspense>
    </RoleNavigationShell>
  );
};
